import React, { useState } from "react";
import { FieldArray, getIn } from "formik";

import {
    Button,
    FormControl,
    InputLabel,
    Select,
    Input,
    TextField,
    MenuItem,
    FormHelperText,
    Grid,
    Paper,
    IconButton,
    InputAdornment,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { alphabets, dateEquators, integerEquators, StringEquators } from "../../../../store/constants/pages";
import { Autocomplete } from '@mui/material';
import { RULE_CRITERIA_DATE, RULE_CRITERIA_INT, RULE_CRITERIA_LONG, RULE_CRITERIA_STRING } from "../../../../store/constants/contract";
import { PopoverLink } from '../../../../common/popoverLink'

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    portlet: {
        'box-shadow': 'none',
    },
    formControl: {
        width: '85%',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(1),
    },
    autoComplete: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    button: {
        margin: theme.spacing(1)
    },
    paper: {
        marginTop: theme.spacing(2),
    },
    iconButton: {
        marginTop: theme.spacing(3),
    },
    clearButton: {
        marginTop: theme.spacing(3),
        marginLeft: '10px',
        color: 'rgba(0, 0, 0, 0.54)'
    },
}));

export default function RuleProviderRoleCriteriaFieldArray({
    groupIndex, groupField, values, handleBlur, handleChange, touched, errors, providerRoleCriteria, setFieldValue, disableCriteriaSelection=false, npiRoles
}) {

    const classes = useStyles();
    const criteriaPath = `${groupField}.providerRolePaymentCriteriaXref`;

    const getHelperText = (index, name) =>
        getIn(touched, `${criteriaPath}.${index}.${name}`) &&
        getIn(errors, `${criteriaPath}.${index}.${name}`);
    
    const hasError = (index, name) =>
        Boolean(
            getIn(touched, `${criteriaPath}.${index}.${name}`) &&
            getIn(errors, `${criteriaPath}.${index}.${name}`)
        );
    

    const handleCriteriaChangeHandler = (index, criteriaObj) => {
        setFieldValue(`${criteriaPath}.${index}.providerRoleSelectionCriteriaId`, criteriaObj);
        setFieldValue(`${criteriaPath}.${index}.type`, criteriaObj?.fieldType || null);
        setFieldValue(
            `${criteriaPath}.${index}.equator`,
            criteriaObj?.id === 1 ? 'equals' : ''
        );
        setFieldValue(`${criteriaPath}.${index}.value`, '');
    };
        

    function getEquators(fieldType) {
        switch (fieldType) {
            case RULE_CRITERIA_STRING:
                return StringEquators.equatorArr
            case RULE_CRITERIA_DATE:
                return dateEquators.equatorArr
            case RULE_CRITERIA_INT:
                return integerEquators.equatorArr
                case RULE_CRITERIA_LONG:
                    return integerEquators.equatorArr
            default:
                return StringEquators.equatorArr
        }
    }

    function hoverColumn(text) {
        return (
            <PopoverLink
                columnData={<i className={`fas fa-question-circle text-info`}></i>}
                hoverData={[
                    {
                        data: text
                    }
                ]}
            />
        );
    }

    return (
        (<FieldArray
            name={criteriaPath}
                render={({ remove, insert }) => (
                    <Grid container>
                        <Grid item xs={12}>
                            {values.providerRolePaymentGroupXref[groupIndex].providerRolePaymentCriteriaXref?.map((selectionCriteria, index) =>
                                <Grid container>
                                    {!disableCriteriaSelection && <Grid item xs={3}>
                                        <Autocomplete
                                        // disabled={disableCriteriaSelection}
                                            className={classes.autoComplete}
                                            value={selectionCriteria.providerRoleSelectionCriteriaId}
                                            disableClearable={false}
                                            onChange={(e, value) => { handleCriteriaChangeHandler(index, value) }}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            options={providerRoleCriteria}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) =>
                                                <TextField {...params}
                                                    name={`${criteriaPath}.${index}.providerRoleSelectionCriteriaId`}
                                                    label="Criteria"
                                                    error={Boolean(getHelperText(index, 'providerRoleSelectionCriteriaId') && hasError(index, 'providerRoleSelectionCriteriaId'))}
                                                    helperText={getHelperText(index, 'providerRoleSelectionCriteriaId') || ''}
                                                    margin="normal" />
                                            }
                                        />
                                    </Grid>}
                                    {selectionCriteria.providerRoleSelectionCriteriaId?.id !== 1 && <>
                                    <Grid item xs={disableCriteriaSelection ?4  :3}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor={`${criteriaPath}.${index}.equator`}>Equator</InputLabel>
                                            <Select
                                                required
                                                value={selectionCriteria?.equator}
                                                onChange={handleChange}
                                                disabled={!selectionCriteria?.providerRoleSelectionCriteriaId}
                                                input={<Input id={`${criteriaPath}.${index}.equator`} />}
                                                inputProps={{
                                                    name: `${criteriaPath}.${index}.equator`,
                                                    id: `${criteriaPath}.${index}.equator`,
                                                }}
                                                error={Boolean(getHelperText(index, 'equator') && hasError(index, 'equator'))}
                                            >
                                                {getEquators(selectionCriteria?.type).map(equator => (
                                                    <MenuItem
                                                        key={equator.value}
                                                        value={equator.value}
                                                    >
                                                        {equator.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText error>{getHelperText(index, 'equator') || ''}</FormHelperText>

                                        </FormControl>
                                    </Grid>
                                    {selectionCriteria?.equator === 'alpha_between' || selectionCriteria?.equator === 'in_between' ?
                                    <Grid container spacing={1} xs={disableCriteriaSelection ?4  :3}>
                                        {selectionCriteria?.type === RULE_CRITERIA_INT || selectionCriteria?.type === RULE_CRITERIA_LONG ?
                                            <>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        type='number'
                                                        name={`${criteriaPath}.${index}.startsAt`}
                                                        className={classes.textField}
                                                        label="Starts at"
                                                        margin="normal"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={selectionCriteria.startsAt}
                                                        error={Boolean(getHelperText(index, 'startsAt') && hasError(index, 'startsAt'))}
                                                        helperText={getHelperText(index, 'startsAt') || ''}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        type='number'
                                                        name={`${criteriaPath}.${index}.endsAt`}
                                                        className={classes.textField}
                                                        label="Ends at"
                                                        margin="normal"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={selectionCriteria.endsAt}
                                                        error={Boolean(getHelperText(index, 'endsAt') && hasError(index, 'endsAt'))}
                                                        helperText={getHelperText(index, 'endsAt') || ''}
                                                    />
                                                </Grid>
                                            </>
                                            : selectionCriteria?.type === RULE_CRITERIA_DATE ?
                                                <>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            type='date'
                                                            name={`${criteriaPath}.${index}.startsAt`}
                                                            className={classes.textField}
                                                            label="Starts at"
                                                            margin="normal"
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            value={selectionCriteria.startsAt}
                                                            error={Boolean(getHelperText(index, 'startsAt') && hasError(index, 'startsAt'))}
                                                            helperText={getHelperText(index, 'startsAt') || ''}
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            type='date'
                                                            name={`${criteriaPath}.${index}.endsAt`}
                                                            className={classes.textField}
                                                            label="Ends at"
                                                            margin="normal"
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            value={selectionCriteria.endsAt}
                                                            error={Boolean(getHelperText(index, 'endsAt') && hasError(index, 'endsAt'))}
                                                            helperText={getHelperText(index, 'endsAt') || ''}
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                        />
                                                    </Grid>
                                                </> :
                                                <>
                                                    <Grid item xs={6}>
                                                        <FormControl className={classes.formControl}>
                                                            <InputLabel htmlFor="startsAt">Starts At</InputLabel>
                                                            <Select
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={selectionCriteria.startsAt}
                                                                error={Boolean(getHelperText(index, 'startsAt') && hasError(index, 'startsAt'))}
                                                                inputProps={{
                                                                    name: `${criteriaPath}.${index}.startsAt`,
                                                                    id: "startsAt"
                                                                }}
                                                            >
                                                                {alphabets.map((item, alphaIndex) => (
                                                                    <MenuItem
                                                                        key={`${alphaIndex}-${item}`}
                                                                        value={item.toLowerCase()}
                                                                    >
                                                                        {item}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            <FormHelperText error>{getHelperText(index, 'startsAt') || ''}</FormHelperText>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <FormControl className={classes.formControl}>
                                                            <InputLabel htmlFor="endsAt">Ends At</InputLabel>
                                                            <Select
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={selectionCriteria.endsAt}
                                                                error={Boolean(getHelperText(index, 'endsAt') && hasError(index, 'endsAt'))}
                                                                inputProps={{
                                                                    name: `${criteriaPath}.${index}.endsAt`,
                                                                    id: "endsAt"
                                                                }}
                                                            >
                                                                {alphabets.map((item, alphaIndex) => (
                                                                    <MenuItem
                                                                        key={`${alphaIndex}-${item}`}
                                                                        value={item.toLowerCase()}
                                                                    >
                                                                        {item}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            <FormHelperText error>{getHelperText(index, 'endsAt') || ''}</FormHelperText>
                                                        </FormControl>
                                                    </Grid>
                                                </>
                                        }
                                    </Grid>
                                    :
                                    <Grid item xs={disableCriteriaSelection ?4  :3}>
                                        <TextField
                                            label="Value"
                                            name={`${criteriaPath}.${index}.value`}
                                            className={classes.textField}
                                            margin="normal"
                                            disabled={selectionCriteria?.equator === ''}
                                            type={selectionCriteria?.type === RULE_CRITERIA_INT ? 'number' : selectionCriteria.type === RULE_CRITERIA_DATE ? 'date' : 'text'}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={selectionCriteria?.value}
                                            InputLabelProps={selectionCriteria.type === RULE_CRITERIA_DATE ?
                                                {
                                                    shrink: true,
                                                } : {

                                                }
                                            }
                                            helperText={getHelperText(index, 'value') || ''}
                                            error={Boolean(getHelperText(index, 'value') && hasError(index, 'value'))}

                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">
                                                    <span style={{pointerEvents: 'auto'}}>
                                                        {hoverColumn(selectionCriteria?.type === RULE_CRITERIA_INT ?
                                                            'Only single numeric value is allowed' :
                                                            'Add single string value or use comma separation for multiple values'
                                                        )}
                                                    </span>
                                                </InputAdornment>
                                            }}
                                        />
                                    </Grid>
                                    }
                                    </>
                                    }
                                    {selectionCriteria.providerRoleSelectionCriteriaId?.id === 1 &&
                                    <Grid item xs={disableCriteriaSelection ?4  :3}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor="role">Select NPI Role</InputLabel>
                                            <Select
                                                required
                                                value={selectionCriteria?.value}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name={`${criteriaPath}.${index}.value`}
                                                disabled={!selectionCriteria?.providerRoleSelectionCriteriaId}
                                                input={<Input id={`${criteriaPath}.${index}.value`} />}
                                                inputProps={{
                                                    name: `${criteriaPath}.${index}.value`,
                                                    id: `${criteriaPath}.${index}.value`,
                                                }}
                                                error={Boolean(getHelperText(index, 'value') && hasError(index, 'value'))}
                                            >
                                                {npiRoles?.map(role => (
                                                    <MenuItem
                                                        key={role.id}
                                                        value={role.id}
                                                    >
                                                        {role.name}
                                                    </MenuItem>
                                                ))}

                                            </Select>
                                            
                                            <FormHelperText error>{getHelperText(index, 'value')}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    }
                                    
                                    <Grid item xs={3}>
                                        <Grid container>
                                        {(index >= 0 && values.providerRolePaymentGroupXref[groupIndex].providerRolePaymentCriteriaXref.length > 1) && (
                                            <IconButton
                                                edge="start"
                                                aria-label="Delete"
                                                onClick={() => remove(index)}
                                                className={classes.iconButton}
                                                size="large">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                        {index === 0 && (
                                            <>
                                            {!disableCriteriaSelection &&  <IconButton
                                                edge="start"
                                                color="primary"
                                                aria-label="Add"
                                                onClick={() => insert(values.providerRolePaymentGroupXref[groupIndex].providerRolePaymentCriteriaXref.length, {
                                                    providerRoleSelectionCriteriaId: null,
                                                    equator: '',
                                                    value: '',
                                                    startsAt: '',
                                                    endsAt: ''
                                                })}
                                                disabled={!selectionCriteria.providerRoleSelectionCriteriaId} 
                                                className={classes.iconButton}
                                                size="large">
                                                <AddCircleIcon />
                                            </IconButton>}
                                            </>
                                        )}
                                        </Grid>
                                    </Grid>
                                    
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}
            />)
    );
}
